import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CalendloTimes } from "@/components/forms/Calendlo";
import { Video, Clock, CheckCircle2 } from "lucide-react";

const ACCENT = "#e8e2d4";
const fmt = (s) => new Date(s).toLocaleString("en-IN", { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) + " IST";

export default function MeetingManage() {
  const { token } = useParams();
  const [m, setM] = useState(null);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState(null);
  const [pick, setPick] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [qs] = useSearchParams();
  const confirm = async () => {
    setBusy(true); setMsg("");
    try { const r = await api.post(`/public/meetings/${token}/confirm`); setM(r.data); }
    catch (e) { setMsg(e.response?.data?.detail || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  useEffect(() => {
    api.get(`/public/meetings/${token}`).then((r) => {
      setM(r.data);
      const a = qs.get("action");
      if (a === "confirm" && r.data.can_change && !r.data.confirmed_at) confirm();
      if (["reschedule", "cancel"].includes(a) && r.data.can_change) setMode(a);
    }).catch(() => setErr("We couldn't find this booking."));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, done) => {
    setBusy(true); setMsg("");
    try { const r = await fn(); if (r?.data?.start) setM(r.data); else setM((x) => ({ ...x, status: "cancelled", can_change: false })); setMode(null); setMsg(done); }
    catch (e) { setMsg(e.response?.data?.detail || "Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0d0d0c] text-[#e8e2d4] flex items-center justify-center p-6" data-testid="meeting-manage-page">
      <div className="w-full max-w-xl space-y-5">
        {err ? <div className="text-lg">{err}</div> : !m ? <div className="opacity-60 text-sm">Loading…</div> : (
          <>
            <div className="text-xs uppercase tracking-widest opacity-50">Thick &amp; Thin</div>
            <div className="text-3xl font-medium" data-testid="meeting-title">{m.title}</div>
            <div className="space-y-1 text-sm opacity-80">
              <div className={m.status === "cancelled" ? "line-through" : ""} data-testid="meeting-when">{fmt(m.start)}</div>
              <div className="flex gap-4"><span className="inline-flex items-center gap-1.5"><Clock size={14} />{m.duration_min} min</span><span className="inline-flex items-center gap-1.5"><Video size={14} />Google Meet</span></div>
              {m.status === "cancelled" && <div className="text-[#ff8a7a]" data-testid="meeting-cancelled">This meeting is cancelled.</div>}
              {m.confirmed_at && m.status !== "cancelled" && <div className="inline-flex items-center gap-1.5 text-[#9be3a5] font-medium" data-testid="meeting-confirmed"><CheckCircle2 size={15} />Confirmed for {fmt(m.start)}</div>}
              {m.meet_url && m.status !== "cancelled" && <a className="underline" href={m.meet_url} target="_blank" rel="noreferrer" data-testid="meeting-join">Join Google Meet</a>}
            </div>
            {msg && <div className="text-sm" data-testid="meeting-msg">{msg}</div>}
            {m.can_change && !mode && (
              <div className="flex flex-wrap gap-3">
                {!m.confirmed_at && <button disabled={busy} className="px-4 py-2.5 rounded-md text-sm font-medium text-black bg-[#9be3a5] disabled:opacity-50" onClick={confirm} data-testid="meeting-confirm">{busy ? "Confirming…" : "Confirm"}</button>}
                <button className="px-4 py-2.5 rounded-md text-sm font-medium text-black" style={{ background: ACCENT }} onClick={() => setMode("reschedule")} data-testid="meeting-reschedule">Reschedule</button>
                <button className="px-4 py-2.5 rounded-md text-sm border border-[#e8e2d455]" onClick={() => setMode("cancel")} data-testid="meeting-cancel">Cancel meeting</button>
              </div>
            )}
            {mode === "reschedule" && (
              <div className="space-y-4">
                <CalendloTimes slug={m.form_slug} blockId={m.block_id} slotsUrl={m.link_slug ? `/public/links/${m.link_slug}/slots` : undefined} value={pick} onChange={setPick} accent={ACCENT} exclude={token} />
                <div className="flex gap-3">
                  <button disabled={!pick || busy} className="px-4 py-2.5 rounded-md text-sm font-medium text-black disabled:opacity-40" style={{ background: ACCENT }}
                    onClick={() => act(() => api.post(`/public/meetings/${token}/reschedule`, { start: pick }), "Done. Your calendar invite has been updated.")} data-testid="meeting-reschedule-confirm">Confirm new time</button>
                  <button className="text-sm opacity-70" onClick={() => setMode(null)}>Back</button>
                </div>
              </div>
            )}
            {mode === "cancel" && (
              <div className="space-y-3">
                <textarea className="w-full bg-transparent border border-[#e8e2d455] rounded-md p-3 text-sm" placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="meeting-cancel-reason" />
                <div className="flex gap-3">
                  <button disabled={busy} className="px-4 py-2.5 rounded-md text-sm font-medium bg-[#ff8a7a] text-black" onClick={() => act(() => api.post(`/public/meetings/${token}/cancel`, { reason }), "Your meeting is cancelled.")} data-testid="meeting-cancel-confirm">Yes, cancel</button>
                  <button className="text-sm opacity-70" onClick={() => setMode(null)}>Keep it</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
